'use client'
import { Authentication } from "@/components/AuthenticationLink"
import { useResponsiveSize } from "@/provider/useResponsiveSize"
import dynamic from "next/dynamic"
import {
  CreateMenuBook,
  CreateMenuCharacter,
  CreateMenuGroup,
  CreateMenuPersona,
  CreateMenuPlus,
  CreateMenuVoice,
} from "@/ui/icons/CreateMenu"
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, cn, useDisclosure } from "@heroui/react"
import { motion } from "framer-motion"
import { useTranslations } from "next-intl"
import { textVariants } from "./SideBar"

// 侧栏全站常驻，声音弹窗依赖链很重（创建页 VoiceSelector、WebSocket 等），
// 静态引入会在每个页面首屏求值；改为打开时才加载
const CreateVoiceModal = dynamic(() => import("@/components/CreateVoiceModal"), { ssr: false })

export const CreateMenuButton = ({ aside }: { aside?: string }) => {
  const t = useTranslations()
  const size = useResponsiveSize(["default", "lg"])
  const menu = useDisclosure()
  const voice = useDisclosure()

  const items = [
    { key: "character", href: "/create/character", icon: <CreateMenuCharacter />, label: t("aside.createMenu.character") },
    { key: "group", href: "/create/group", icon: <CreateMenuGroup />, label: t("aside.createMenu.group") },
    { key: "lorebook", href: "/create/lorebook", icon: <CreateMenuBook />, label: t("aside.createMenu.lorebook"), beta: true },
    { key: "persona", href: "/create/persona", icon: <CreateMenuPersona />, label: t("aside.createMenu.persona") },
    { key: "voice", icon: <CreateMenuVoice />, label: t("aside.createMenu.voice") },
  ] as const

  return (
    <Authentication
      render={(verify) => (
        <>
          <Dropdown
            isOpen={menu.isOpen}
            onOpenChange={(open) => {
              if (open && !verify()) return
              menu.onOpenChange(open)
            }}
            placement={size === "default" ? "bottom-start" : "right-start"}
            offset={8}
            classNames={{
              content: "z-[60] w-[188px] min-w-[188px] overflow-visible rounded-xl bg-gray-bg p-1 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.15)]",
            }}
          >
            <DropdownTrigger>
              <Button
                data-aside={aside}
                className={cn(
                  "create-character-btn relative z-[1] flex h-10 min-w-[40px] justify-start bg-[#001d35] text-[14px] font-medium text-white",
                  "rounded-[12px] border border-[#4886ff] data-[aside=default]:rounded-full",
                  "px-[8px] data-[aside=default]:pl-[24px] data-[aside=default]:pr-[16px]",
                )}
                startContent={<CreateMenuPlus className="shrink-0" />}
                fullWidth
              >
                <motion.span variants={textVariants}>{t("create.title")}</motion.span>
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label={t("create.title")}
              itemClasses={{
                base: "h-10 gap-2 rounded-xl px-4 text-[14px] font-medium text-theme-primary data-[hover=true]:bg-seleced-bg",
              }}
            >
              {items.map(item => (
                <DropdownItem
                  key={item.key}
                  href={"href" in item ? item.href : undefined}
                  textValue={item.label}
                  startContent={item.icon}
                  onPress={"href" in item ? undefined : voice.onOpen}
                >
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <span className="truncate">{item.label}</span>
                    {"beta" in item ? (
                      <span className="shrink-0 rounded-xl border-[0.7px] border-[#313b4c] px-2 py-0.5 text-[12px] font-medium capitalize leading-none text-[#313b4c] dark:border-theme-primary dark:text-theme-primary">
                        {t("aside.createMenu.beta")}
                      </span>
                    ) : null}
                  </span>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          {voice.isOpen ? <CreateVoiceModal {...voice} resetData={{}} /> : null}
        </>
      )}
    />
  )
}
